import type { Connection } from "@zworks/console-contracts";

/**
 * The overview strip's figures, derived from the connections the API returned.
 *
 * Derived rather than returned by an endpoint, because every value is already on the list the page
 * has. A second endpoint would be a second source of truth, and the two would disagree the moment
 * one of them changed.
 */
export interface ConnectionOverview {
  /** Connections that are active: a revoked or expired row exists but is not connected. */
  connected: number;
  /** Connections somebody must act on. */
  needsAttention: number;
  /** Connections whose row names a credential reference. */
  credentials: number;
}

/** A connection is connected when it is active. Everything else is a row with a lifecycle. */
const connectedStatus = "active";
/** Statuses that mean the connection is not working and somebody must act. */
export const attentionStatuses = new Set(["failing", "blocked", "expired"]);
/** Statuses of a connection that no longer exists in any useful sense. */
const retiredStatuses = new Set(["revoked", "expired"]);

export function connectionOverview(connections: readonly Connection[]): ConnectionOverview {
  let connected = 0;
  let needsAttention = 0;
  let credentials = 0;
  for (const connection of connections) {
    if (connection.status === connectedStatus) connected += 1;
    if (attentionStatuses.has(connection.status) || connection.health === "unreachable") {
      needsAttention += 1;
    }
    // The reference, not the auth method: an allowlist entry has a method and no secret, and a
    // pending app connection has a method and no reference yet.
    if (connection.credentialRef) credentials += 1;
  }
  return { connected, needsAttention, credentials };
}

/** The five sections of the page. */
export type IntegrationTab = "overview" | "connected" | "available" | "activity" | "policies";

/** Whether a catalogue entry has a live connection behind it. */
export type CatalogueStatus = "not-connected" | "planned" | "connected";

/**
 * A catalogue entry is `connected` only when an active connection names it.
 *
 * A revoked connection is still a row, so matching on the type alone would show a green Connected
 * badge beside a card that reads Revoked. "Not connected yet" means the code path exists and nothing
 * is set up; "planned" means the platform cannot do it at all, and the guide's own status supplies
 * that distinction.
 */
export function catalogueStatus(slug: string, connections: readonly Connection[]): CatalogueStatus {
  return connections.some(
    (connection) =>
      connection.integrationType === slug &&
      connection.status === connectedStatus &&
      !retiredStatuses.has(connection.status),
  )
    ? "connected"
    : "not-connected";
}

/** The access a connection asks for, phrased as what the platform will be able to do. */
export interface AccessLine {
  scope: string;
  summary: string;
}

const scopeSummaries: Record<string, string> = {
  "repo:read": "Read your repositories and their contents.",
  "repo:write": "Push branches and open pull requests in the repositories you choose.",
  "issue:write": "Open and comment on issues.",
  "status:read": "Read commit statuses and check results.",
  "model:invoke": "Send prompts to the model provider on this project's behalf, billed to its key.",
  "model:read": "List the models this key may call.",
  "webhooks:send": "Send signed event deliveries to the endpoint you register.",
  "webhooks:receive": "Receive signed event deliveries from the external system.",
};

/**
 * Renders each requested scope as a sentence. An unknown scope is shown verbatim rather than
 * dropped: the wizard's whole point is that nothing is authorised before it is legible, and a
 * silently omitted scope is exactly the one that surprises the user later.
 */
export function accessLines(scopes: readonly string[]): AccessLine[] {
  return scopes.map((scope) => ({
    scope,
    summary: scopeSummaries[scope] ?? `Grant the ${scope} permission.`,
  }));
}

/**
 * Whether the wizard may move from its access step to authorising.
 *
 * It needs at least one scope to show, and the user's explicit acknowledgement. An empty scope list
 * is a registry entry that is misconfigured, and authorising it would create a connection that can do
 * nothing while looking healthy.
 */
export function canAuthorise(scopes: readonly string[], acknowledged: boolean): boolean {
  return scopes.length > 0 && acknowledged;
}
